import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import { getQbotAccessToken } from '@/lib/logto';
import type {
  BattlesResponse,
  CoachBuildResponse,
  CoachDeckResponse,
  CoachMatchupResponse,
  CrIdentity,
  DeckCheckResponse,
  LinkPlayerResponse,
  PopularDecksResponse,
  ProfileResponse,
  PublicCard,
  SavedDeck,
} from '@/types/qbot';

/**
 * QBot API client. Talks to qbot-api at /api/v1/cr/*.
 *
 * Auth: Logto Bearer JWT with audience `https://qbot.qlash.gg`, minted
 * by getQbotAccessToken() via refresh-token grant (same pattern as
 * qriotsApi). The audience is the QBot backoffice resource — already
 * registered on the staging+prod Logto tenants — so no new Logto setup
 * is required for the Mini App.
 *
 * Dev: env.qbotApiUrl = '/qbot-api', Vite proxy forwards to MacMini.
 * Prod: env.qbotApiUrl = 'https://qbot.qlash.gg'.
 *
 * Tag invalidation:
 *   - 'Identity' refetches `/me` after link/unlink
 *   - 'Profile' refetches `/profile/me` after link
 *   - 'SavedDecks' refetches `/decks/saved` after create/delete
 */
export const qbotApi = createApi({
  reducerPath: 'qbotApi',
  baseQuery: retry(
    fetchBaseQuery({
      baseUrl: env.qbotApiUrl,
      prepareHeaders: async (headers) => {
        const token = await getQbotAccessToken();
        if (token) headers.set('Authorization', `Bearer ${token}`);
        headers.set('Accept', 'application/json');
        return headers;
      },
    }),
    { maxRetries: 1 },
  ),
  keepUnusedDataFor: 300,
  refetchOnFocus: false,
  refetchOnReconnect: true,
  tagTypes: ['Identity', 'Profile', 'SavedDecks'],
  endpoints: (builder) => ({
    // ─── Identity + card catalog ────────────────────────────────────────

    me: builder.query<CrIdentity, void>({
      query: () => '/api/v1/cr/me',
      transformResponse: (raw: { data: CrIdentity }) => raw.data,
      providesTags: ['Identity'],
    }),

    cards: builder.query<PublicCard[], void>({
      query: () => '/api/v1/cr/cards',
      transformResponse: (raw: { data: { cards: PublicCard[]; count: number } }) =>
        raw.data.cards,
      keepUnusedDataFor: 86400, // 24h — catalog is near-static
    }),

    // ─── Profile ────────────────────────────────────────────────────────

    profileMe: builder.query<ProfileResponse, void>({
      query: () => '/api/v1/cr/profile/me',
      transformResponse: (raw: { data: ProfileResponse }) => raw.data,
      providesTags: ['Profile'],
    }),

    profileByTag: builder.query<ProfileResponse, { tag: string }>({
      query: ({ tag }) => `/api/v1/cr/profile/${encodeURIComponent(tag)}`,
      transformResponse: (raw: { data: ProfileResponse }) => raw.data,
    }),

    linkPlayer: builder.mutation<LinkPlayerResponse, { tag: string }>({
      query: (body) => ({ url: '/api/v1/cr/profile/link', method: 'POST', body }),
      transformResponse: (raw: { data: LinkPlayerResponse }) => raw.data,
      invalidatesTags: ['Identity', 'Profile'],
    }),

    unlinkPlayer: builder.mutation<{ unlinked: boolean; removedRows: number }, void>({
      query: () => ({ url: '/api/v1/cr/profile/link', method: 'DELETE' }),
      transformResponse: (raw: { data: { unlinked: boolean; removedRows: number } }) => raw.data,
      invalidatesTags: ['Identity', 'Profile'],
    }),

    // ─── Decks ──────────────────────────────────────────────────────────

    popularDecks: builder.query<
      PopularDecksResponse,
      { archetype?: string; cards?: string[]; page?: number; pageSize?: number }
    >({
      query: ({ archetype, cards, page = 1, pageSize = 10 }) => ({
        url: '/api/v1/cr/decks/popular',
        params: {
          ...(archetype ? { archetype } : {}),
          ...(cards?.length ? { cards: cards.join(',') } : {}),
          page,
          pageSize,
        },
      }),
      transformResponse: (raw: { data: PopularDecksResponse }) => raw.data,
      keepUnusedDataFor: 1800, // 30 min — popular decks are server-side cached for 30 min too
    }),

    deckCheck: builder.query<DeckCheckResponse, { tag: string }>({
      query: ({ tag }) => `/api/v1/cr/decks/check/${encodeURIComponent(tag)}`,
      transformResponse: (raw: { data: DeckCheckResponse }) => raw.data,
    }),

    savedDecks: builder.query<{ decks: SavedDeck[]; count: number }, void>({
      query: () => '/api/v1/cr/decks/saved',
      transformResponse: (raw: { data: { decks: SavedDeck[]; count: number } }) => raw.data,
      providesTags: ['SavedDecks'],
    }),

    saveDeck: builder.mutation<{ deck: SavedDeck }, { name: string; cards: string[] }>({
      query: (body) => ({ url: '/api/v1/cr/decks/saved', method: 'POST', body }),
      transformResponse: (raw: { data: { deck: SavedDeck } }) => raw.data,
      invalidatesTags: ['SavedDecks'],
    }),

    deleteSavedDeck: builder.mutation<{ deleted: boolean; id: number }, { id: number }>({
      query: ({ id }) => ({ url: `/api/v1/cr/decks/saved/${id}`, method: 'DELETE' }),
      transformResponse: (raw: { data: { deleted: boolean; id: number } }) => raw.data,
      invalidatesTags: ['SavedDecks'],
    }),

    // ─── Battles ────────────────────────────────────────────────────────

    battles: builder.query<
      BattlesResponse,
      { tag: string; page?: number; pageSize?: number; analyze?: boolean }
    >({
      query: ({ tag, page = 1, pageSize = 10, analyze = false }) => ({
        url: `/api/v1/cr/battles/${encodeURIComponent(tag)}`,
        params: { page, pageSize, ...(analyze ? { analyze: 'true' } : {}) },
      }),
      transformResponse: (raw: { data: BattlesResponse }) => raw.data,
    }),

    // ─── AI coaching (pro tier required) ────────────────────────────────

    coachDeck: builder.mutation<CoachDeckResponse, { tag: string }>({
      query: ({ tag }) => ({ url: `/api/v1/cr/coach/deck/${encodeURIComponent(tag)}`, method: 'POST' }),
      transformResponse: (raw: { data: CoachDeckResponse }) => raw.data,
    }),

    coachBuild: builder.mutation<CoachBuildResponse, { style?: string; keptCards?: string[] }>({
      query: (body) => ({ url: '/api/v1/cr/coach/build', method: 'POST', body }),
      transformResponse: (raw: { data: CoachBuildResponse }) => raw.data,
    }),

    coachMatchup: builder.mutation<
      CoachMatchupResponse,
      { myTag: string; opponentTag: string; gameMode?: string }
    >({
      query: (body) => ({ url: '/api/v1/cr/coach/matchup', method: 'POST', body }),
      transformResponse: (raw: { data: CoachMatchupResponse }) => raw.data,
    }),
  }),
});

export const {
  useMeQuery,
  useCardsQuery,
  useProfileMeQuery,
  useProfileByTagQuery,
  useLinkPlayerMutation,
  useUnlinkPlayerMutation,
  usePopularDecksQuery,
  useDeckCheckQuery,
  useSavedDecksQuery,
  useSaveDeckMutation,
  useDeleteSavedDeckMutation,
  useBattlesQuery,
  useCoachDeckMutation,
  useCoachBuildMutation,
  useCoachMatchupMutation,
} = qbotApi;

/**
 * Build a CDN-ish URL for a card icon served by qbot-api.
 * Used by <CardIcon> to render PNG art without bundling the assets.
 */
export function qbotCardIconUrl(id: number, form: 'base' | 'evolution' | 'hero' = 'base'): string {
  const q = form === 'base' ? '' : `?form=${form}`;
  return `${env.qbotApiUrl}/api/v1/cr/cards/${id}/icon${q}`;
}
